import React, { useEffect, useState } from 'react';
import { callAPI } from '../utils/apiUtils';
import { apiUrls } from '../utils/apiUrls';
import { getSessionId } from '../utils/sessionId';
import '../styles/upfrontOrderSetup.css';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getActiveServerId } from '../utils/staffSession';
import { useConnection } from '../offline/connection';
import { canDo } from '../offline/state';
import { getUpfrontSetup, placeUpfrontOrder, clearUpfrontSetup, describeUpfrontSetup } from '../utils/upfrontSetup';

const OrderReviewSidebar = ({
  netTotal,
  orderType,
  selectedTables,
  existing_order_id = null,
  selected_customer_id = null,
  selected_table_ids = null,
  serverId,
  servers = [],
  editOrderId =null,
  initialServiceType = null,
  isPagerOrder = false
}) => {
  // The one place a server is actually required, so it is resolved here rather than trusted from
  // the prop. A PIN-identified staff member IS the server: the dine-in path already short-circuits
  // on this in TakeawayCart::handleQuickSendToKitchen and never reaches this screen, which is why
  // only express/takeaway/delivery kept asking.
  //
  // `||` and not `??` on purpose. 205,130 takeaway orders in the estate carry server_id = 0 rather
  // than NULL, so re-opening one from the Orders list hands this a literal 0 - which `??` keeps and
  // every truthiness check downstream then rejects.
  const resolveServerId = () => serverId || getActiveServerId() || null;
  // Offline, delivery needs addresses and fee bands the device does not have: only what the offline engine can do is offered.
  const { state: offlineState } = useConnection();
  const [subOrderTypes, setSubOrderTypes] = useState([]);
  const [subOrderType, setSubOrderType] = useState('take_away');
  const [orderDay, setOrderDay] = useState('today');
  const [orderDate, setOrderDate] = useState(getMinDate());
  const [orderTime, setOrderTime] = useState('');
  const [timeSlots, setTimeSlots] = useState([]);

  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [orderName, setOrderName] = useState('');
  // A Pager order isn't really "dine-in" in the entree-pacing sense those 4 options assume - the
  // customer isn't seated yet - so it swaps them for a simple Takeaway/Dine-in choice instead,
  // printed on the docket via the exact same "MEALS - <label>" line (see
  // Order::SERVICE_TYPES/getServiceTypeName(), read by every ESCPOSNewFormatPrintableV* class
  // already, so no printer-side change is needed for either category). isPagerOrder comes from
  // OrderReview.js's tabId (not derivable from selectedTables here - its shape is ambiguous by
  // the time it reaches this page, see OrderReview.js's own comment on it).
  const [serviceType, setServiceType] = useState(
    initialServiceType || (isPagerOrder ? 'pager_takeaway' : 'entree_first')
  );
  const navigate = useNavigate();
  const [hasExistingOrder, setHasExistingOrder] = useState(false);
  const [resolvedExistingOrderId, setResolvedExistingOrderId] = useState(null);
  const [isExistingOrderPrepaid, setIsExistingOrderPrepaid] = useState(false);
  // "Customer details first" (Settings): the service, time, customer and address were already collected on the Takeaway / Delivery
  // tab, so this page does not ask for them again and Next places the order from them and goes straight to payment.
  const upfrontSetup = orderType === 'takeaway' && !editOrderId && !existing_order_id ? getUpfrontSetup() : null;

  function getMinDate() {
    const date = new Date();
    date.setDate(date.getDate() + 2);
    return date.toISOString().split('T')[0];
  }

  useEffect(() => {
    if (orderType === 'takeaway') {
      fetchOrderTypes();
    }
  }, [orderType]);

  useEffect(() => {
    if (orderType === 'takeaway') {
      setOrderDay('today');
      setOrderTime('');
      fetchTimeSlots(orderDay);
    }
  }, [subOrderType]);

  useEffect(() => {
    if (orderType === 'takeaway') {
      const day = orderDay === 'other' ? orderDate : orderDay;
      fetchTimeSlots(day);
    }
  }, [orderDay, orderDate]);

  const fetchOrderTypes = async () => {
    const session_id = getSessionId();
    try {
      const response = await callAPI(
        `${apiUrls.GET_OERDER_TYPE}?session_id=${session_id}`,
        {},
        'GET'
      );
      const data = response?.data?.data || response?.data || [];
      setSubOrderTypes(data);
    } catch (error) {
      console.error('Error fetching order types:', error);
      setSubOrderTypes([]);
    }
  };

  const fetchTimeSlots = async (value) => {
    try {
      const session_id = getSessionId();
      const url = `${apiUrls.GET_TIME_SLOTS}?type=${subOrderType}&day=${value}&session_id=${session_id}`;
      const response = await callAPI(url, {}, 'GET');
      const slots = response?.data?.data || response?.data || [];
      setTimeSlots(slots);
      setOrderTime(slots[0]?.slot || '');
    } catch (error) {
      console.error('Error fetching time slots:', error);
      setTimeSlots([]);
      setOrderTime('');
    }
  };


  const fetchExistingOrderCustomer = async () => {
    try {
      const payload = {
        existing_order_id: existing_order_id ?? editOrderId ?? null,
        selected_tables: selectedTables || [],
        server_id: resolveServerId()
      };
  
      const response = await callAPI(
        apiUrls.GET_ORDER_CUSTOMER_DETAILS,
        {},
        'POST',
        payload
      );
  
      if (response?.data?.status === 1) {
        setCustomerName(response.data.data.cust_name || '');
        setCustomerMobile(response.data.data.cust_mobile || '');
        setOrderName(response.data.data.order_name || '')
        setIsExistingOrderPrepaid(!!response.data.data.is_prepaid);

        if (response.data.data.existing_order_id) {
          setHasExistingOrder(true);
          setResolvedExistingOrderId(response.data.data.existing_order_id);
        }
      }
    } catch (error) {
      console.error('Error fetching customer details:', error);
    }
  };

  useEffect(() => {
    console.log("existing_order_id",existing_order_id);
    console.log("editOrderId",editOrderId);
    console.log("orderType", orderType);
    if (orderType === 'dinein' && (existing_order_id || selectedTables?.length || editOrderId)) {
      fetchExistingOrderCustomer();
    }
  }, [existing_order_id, selectedTables, serverId, orderType]);

  
  const handleSendToKitchen = async (customCheckoutType = null) => {
    const hasServers = Array.isArray(servers) && servers.length > 0;
    const effectiveServerId = resolveServerId();

    if (hasServers && !effectiveServerId) {
      toast.error('Please select a server');
      return;
    }
    if (upfrontSetup) {
      const result = await placeUpfrontOrder({ setup: upfrontSetup, serverId: effectiveServerId, sessionId: getSessionId() });
      if (!result.ok) {
        toast.error(result.message);
        if (result.orderCreated) clearUpfrontSetup();
        return;
      }
      if (result.warning) toast.warn(result.warning);
      clearUpfrontSetup();
      navigate('/payment-view?order_id=' + result.orderId, { state: { customer_id: result.customerId, order_id: result.orderId } });
      return;
    }
    if (customerMobile.trim() && !/^[0-9]{10}$/.test(customerMobile)) {
      return alert("Please enter a valid 10-digit mobile number!");
    }
    const finalCheckoutType = customCheckoutType 
  ? customCheckoutType 
  : (orderType === 'takeaway' ? 'phone-order' : 'finalise-order');

    const session_id = getSessionId();
    const payload = {
      server_id: effectiveServerId,
      //checkout_type: orderType === 'takeaway' ? 'phone-order' : 'finalise-order',
      checkout_type: finalCheckoutType,
      preferred_order_day: orderDay === 'other' ? orderDate : orderDay,
      preferred_order_time: orderTime,
      //order_type:   orderType === 'takeaway' ? 'take_away' : orderType === 'dinein' ? 'dine_in'  : orderType,
      order_type: orderType === 'takeaway' && subOrderType === 'delivery'
      ? 'delivery'
      : orderType === 'takeaway'
      ? 'take_away'
      : orderType === 'dinein'
      ? 'dine_in'
      : orderType,
      sub_order_type: subOrderType,
      service_type: orderType === 'dinein' ? serviceType : '',
      order_name: orderName,
      cust_mobile: customerMobile,
      cust_name: customerName,
      selected_tables: selectedTables, 
      session_id:session_id
    };
    //if (existing_order_id)      payload.existing_order_id = existing_order_id;
    if (existing_order_id || resolvedExistingOrderId) {
      payload.existing_order_id = existing_order_id || resolvedExistingOrderId;
    }
    if (selected_customer_id)   payload.selected_customer_id = selected_customer_id;
    if (selected_table_ids)     payload.selected_table_ids = selected_table_ids;
    // edit_order_id deliberately not sent - see TakeawayCart::submitSendToKitchen. It routes the
    // API into editPosOrder(), which prints no kitchen docket. existing_order_id above carries the
    // table association.
    
    console.log(payload);
    try {
      const response = await callAPI(apiUrls.ORDER_CREATE, {}, 'POST', payload, {});
      
      console.log(response);
      if (response?.data?.status === 1) {
        const orderTypeFromResponse = response?.data?.data?.order_type;
        const orderId = response?.data?.data?.order_id;
        if (orderId) {
          localStorage.setItem('current_order_id', orderId);
        }
        if (finalCheckoutType === 'makepayment') {
          console.log("make_payment,selected_table_ids", selected_table_ids)
          //navigate(`/payment-view?table_id=${selectedTables?.[0]}`);
          navigate(`/payment-view?table_id=${selectedTables?.[0]}`, {
            state: {
              table_id: selectedTables?.[0],
              is_prepaid: true
            }
          });
          
          return; 
        }
      
        //kahi to rakhna hi padega nahi to refresh pe kya karenge
        const isSuccessfulEditOrder = response?.data?.isSuccessful === true || response?.data?.isSuccessful === "true";

        if (orderTypeFromResponse === '3' || orderTypeFromResponse === '4') {
          navigate('/dashboard');
        } else if (orderTypeFromResponse === '1' || orderTypeFromResponse === '2') {
          navigate('/select-customer', { state: { order_id: orderId } });
        } else if (orderType === 'dinein' && isSuccessfulEditOrder && editOrderId) {

          const customerId =  response?.data?.customer_id;
          localStorage.setItem('current_customer_id', customerId);
          navigate('/dashboard');

        } else if(isSuccessfulEditOrder && editOrderId) {

          const customerId =  response?.data?.customer_id;
          localStorage.setItem('current_customer_id', customerId);
          const navState = {
            customer_id: customerId,
            order_id: editOrderId,
          };
           navigate('/payment-view?order_id='+editOrderId, { state: navState })

        }else {
          navigate('/dashboard');
        }
      } else {
        // The backend only rejects order-create outright for session/cart-state problems it
        // can't recover from here (e.g. "No items selected", "Table is Not Selected... Please
        // go back/home page and Start Fresh!") - staying on this page can't fix those, it just
        // leaves the user stuck on a dead-end screen. Redirect home after showing why.
        toast.error(response?.data?.message);
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Send to kitchen error:', error);
      toast.error(error);
     // alert('Failed to send order. Try again later.');
    }
  };

  return (
    <div className="col-lg-4">
      <div className="cardStyle paymentDetailSidebar mb-3">
        <div className="cardHeader">
          <h4 className="smTitle mb-0">Checkout</h4>
        </div>
        <div className="cardBody">
          <form className="formStyle">
          {(() => {
            const isFreshOrder = !existing_order_id && !hasExistingOrder;
            const isPrepaidOrder = isExistingOrderPrepaid;
            return (
              <>
          {orderType === 'dinein' && (isFreshOrder || isPrepaidOrder) && (
            <div className="mt-3">
              <button
                type="button"
                className="btn btnPrimary w-100"
                onClick={() => handleSendToKitchen('makepayment')}
              >
                PREPAID (Pay ${netTotal})
              </button>
            </div>
          )}
          {/* A pager is always prepaid at the counter - there's no separate "send it through,
              pay later" path for it the way a seated dine-in table has, so this button (Send to
              Kitchen for dine-in, Next for takeaway/phone) is dropped entirely for pager orders,
              leaving PREPAID above as the only option. */}
          {(orderType !== 'dinein' || isFreshOrder || !isPrepaidOrder) && !isPagerOrder && (
          <div className="mt-4">
              <button
                type="button"
                className="btn btnPrimary w-100"
                onClick={() => handleSendToKitchen()}
              >
                <span>    {orderType === 'dinein'
      ? `SEND TO KITCHEN (Pay $${netTotal})`
      : `NEXT (Pay $${netTotal})`}</span>
              </button>
            </div>
          )}
              </>
            );
          })()}
            {orderType === 'dinein' && (
              <>
              <div className="mb-3 mt-4">
                  <label className="form-label  fw-semibold">Service Type</label>
                  <div className="d-flex flex-wrap gap-3">
                    {isPagerOrder ? (
                      <>
                        <div>
                          <input
                            type="radio"
                            id="service-pager-takeaway"
                            value="pager_takeaway"
                            name="service_types"
                            checked={serviceType === 'pager_takeaway'}
                            onChange={(e) => setServiceType(e.target.value)}
                          />
                          <label htmlFor="service-pager-takeaway" className="ms-2 me-4">Takeaway</label>
                        </div>
                        <div>
                          <input
                            type="radio"
                            id="service-pager-dinein"
                            value="pager_dine_in"
                            name="service_types"
                            checked={serviceType === 'pager_dine_in'}
                            onChange={(e) => setServiceType(e.target.value)}
                          />
                          <label htmlFor="service-pager-dinein" className="ms-2">Dine-in</label>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                        <input
                          type="radio"
                          id="customRadio1"
                          value="entree_first"
                          name="service_types"
                          checked={serviceType === 'entree_first'}
                          onChange={(e) => setServiceType(e.target.value)}
                        />
                        <label htmlFor="customRadio1" className="ms-2 me-4">Entree First</label>
                        </div>
                        <div>
                        <input
                          type="radio"
                          id="customRadio2"
                          value="all_together"
                          name="service_types"
                          checked={serviceType === 'all_together'}
                          onChange={(e) => setServiceType(e.target.value)}
                        />
                        <label htmlFor="customRadio2" className="ms-2">All Together</label>
                        </div>
                        <div>
                          <input
                            type="radio"
                            id="service-running"
                            value="running"
                            name="service_types"
                            checked={serviceType === 'running'}
                            onChange={(e) => setServiceType(e.target.value)}
                          />
                          <label htmlFor="service-running" className="ms-2">Running</label>
                        </div>

                        <div>
                          <input
                            type="radio"
                            id="service-ignore"
                            value="ignore"
                            name="service_types"
                            checked={serviceType === 'ignore'}
                            onChange={(e) => setServiceType(e.target.value)}
                          />
                          <label htmlFor="service-ignore" className="ms-2">Ignore</label>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Customer Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Customer Mobile</label>
                  <input
                    type="tel"
                    className="form-control"
                    maxLength={10}
                    value={customerMobile}
                    onChange={(e) => setCustomerMobile(e.target.value)}
                  />
                </div>

                {/* <div className="mb-3">
                  <label className="form-label">Name the Order</label>
                  <input
                    type="text"
                    className="form-control"
                    value={orderName}
                    onChange={(e) => setOrderName(e.target.value)}
                  />
                </div> */}

                
              </>
            )}

            {upfrontSetup && (
              <div className="col-12 mt-4">
                <p className="fw-medium mb-1">Order details</p>
                <p className="mb-1">{describeUpfrontSetup(upfrontSetup)}</p>
                {upfrontSetup.address && <p className="mb-0">{upfrontSetup.address.full_address}</p>}
                <p className="upfrontMuted mt-2">Change them from the Takeaway / Delivery tab.</p>
              </div>
            )}

            {orderType === 'takeaway' && !editOrderId && !upfrontSetup &&(
              <>
                <div className="col-12 mt-4">
                  <p className="fw-medium mb-2">Order Type</p>
                  {subOrderTypes.filter((type) => type.type !== 'delivery' || canDo(offlineState, 'delivery')).map((type, index) => (
                    <div className="form-check mb-2" key={index}>
                      <input
                        className="form-check-input"
                        type="radio"
                        name="subOrderType"
                        id={`subOrderType-${type.type}`}
                        value={type.type}
                        checked={subOrderType === type.type}
                        onChange={(e) => setSubOrderType(e.target.value)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`subOrderType-${type.type}`}
                      >
                        {type.name}
                      </label>
                    </div>
                  ))}
                </div>

                <div className="mb-3">
                  <label className="form-label fw-medium">Select Day</label>
                  <select
                    className="form-select"
                    value={orderDay}
                    onChange={(e) => setOrderDay(e.target.value)}
                  >
                    <option value="today">Today</option>
                    <option value="tomorrow">Tomorrow</option>
                    <option value="other">Pre-Order</option>
                  </select>
                </div>

                {orderDay === 'other' && (
                  <div className="mb-3">
                    <label className="form-label">Select Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={orderDate}
                      min={getMinDate()}
                      onChange={(e) => setOrderDate(e.target.value)}
                    />
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Select Time</label>
                  <select
                    className="form-select"
                    value={orderTime}
                    onChange={(e) => setOrderTime(e.target.value)}
                  >
                    <option value="">-- Please Select Time --</option>
                    {timeSlots.map((slot, index) => (
                      <option key={index} value={slot.slot}>
                        {slot.slot}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

    
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrderReviewSidebar;
